/**
 * ThreadsSection — project-scoped Threads list.
 *
 * Renders in two states:
 *   'home'     — root:threads node in the multi-project home view
 *   'rootZoom' — full thread list when Threads is the zoom root
 *
 * Data: loads ctx.threadCache once per project, sorted by Last Activity desc.
 */

import { useState, useEffect, useReducer, useRef } from 'react'
import { wf, saveViewState } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { getProjectThreads } from '../services/threads.js'
import ThreadNode from './ThreadNode.jsx'
import ThreadCategoryList, { ThreadCountsMeta } from './ThreadCategoryList.jsx'
import CreateThreadModal from './CreateThreadModal.jsx'

/* ── Visibility ─────────────────────────────────────────────────────────── */

function calcThreadsVis() {
  if (!wf.zoomProjectId) return 'hidden'
  // Category zoom: sec:threads:cat:<Type>
  if (wf.zoomSection && wf.zoomSection.indexOf('sec:threads:cat:') === 0 &&
      !wf.zoomPhaseId && !wf.zoomMsId &&
      !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId && !wf.searchQuery) return 'catZoom'
  // Individual thread zoom: thread-detail:<id>
  if (wf.zoomSection && wf.zoomSection.indexOf('thread-detail:') === 0 &&
      !wf.zoomPhaseId && !wf.zoomMsId &&
      !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId && !wf.searchQuery) return 'detailZoom'
  if (wf.zoomRoot === 'threads' && !wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomSection &&
      !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId && !wf.searchQuery) return 'rootZoom'
  if (wf.zoomRoot === 'reference' && !wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomSection &&
      !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId && !wf.searchQuery) return 'referenceZoom'
  if (!wf.zoomRoot && !wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomSection &&
      !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId && !wf.searchQuery) return 'home'
  return 'hidden'
}

/* ── Filler row (loading / empty states, standard outline grid) ─────────── */

function FillerRow({ text, italic }) {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ color: '#7a9aaa', ...(italic ? { fontStyle: 'italic' } : {}) }}>{text}</span>
        </div>
      </div>
    </div>
  )
}

/* ── Thread list content (shared between home expanded + rootZoom) ──────
 * Threads group by Type into category branch nodes (ThreadCategoryList);
 * each category zooms to sec:threads:cat:<Type>. */

function ThreadListContent({ threads, projectId, projectName }) {
  if (!threads) return <FillerRow text="Loading…" />
  if (threads.length === 0) return <FillerRow text="No threads yet." italic />
  return (
    <ThreadCategoryList
      threads={threads}
      projectId={projectId}
      projectName={projectName}
      scopeKey="thcat:all"
      onZoomCategory={type => ctx.zoomToSection('sec:threads:cat:' + type, { root: 'threads' })}
    />
  )
}

/* ── Main component ─────────────────────────────────────────────────────── */

export default function ThreadsSection() {
  const [, forceUpdate]      = useReducer(v => v + 1, 0)
  const [vis, setVis]        = useState(calcThreadsVis)
  const [recordId, setRecordId] = useState(() => wf.zoomProjectId || null)
  const [showCreate, setShowCreate] = useState(false)
  const hasLoadedRef = useRef(false)

  // Derive project name from loaded milestones (same approach as ProductionSection)
  const projectName = ctx.state?.milestones?.[0]?.fields?.['Project Name'] || ''

  function maybeLoad(projId) {
    if (!projId) return
    if (hasLoadedRef.current) return
    hasLoadedRef.current = true
    if (ctx.threadCache !== undefined) return
    ctx.threadCache = null
    getProjectThreads(projId).then(records => {
      ctx.threadCache = records
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => {
      ctx.threadCache = []
      window.dispatchEvent(new Event('outline-render'))
    })
  }

  // Reset + reload on project change
  useEffect(() => {
    if (!recordId) return
    ctx.threadCache = undefined
    hasLoadedRef.current = false
    maybeLoad(recordId)
  }, [recordId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to outline-render for visibility + recordId updates
  useEffect(() => {
    function onRender() {
      const newRecordId = wf.zoomProjectId || null
      setRecordId(prev => {
        if (prev !== newRecordId) return newRecordId
        return prev
      })
      setVis(calcThreadsVis())
      forceUpdate()
    }
    function onAdd() { setShowCreate(true) }
    window.addEventListener('outline-render', onRender)
    window.addEventListener('open-threads-add', onAdd)
    return () => {
      window.removeEventListener('outline-render', onRender)
      window.removeEventListener('open-threads-add', onAdd)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (vis === 'hidden') return null

  const threads = ctx.threadCache  // null = in-flight, undefined = not started, [...]= loaded
  const rootCollapsed = wf.collapsed['root:threads'] !== false

  /* ── Category zoom — children-only thread rows of one Type ────────────── */

  if (vis === 'catZoom') {
    const type = wf.zoomSection.slice('sec:threads:cat:'.length)
    const list = Array.isArray(threads)
      ? threads.filter(t => (t.fields['Type'] || 'Misc. Items') === type)
      : null
    return (
      <div className="wf-list">
        {!list && <FillerRow text="Loading…" />}
        {list && list.length === 0 && <FillerRow text="No threads in this category." italic />}
        {list && list.map(t => (
          <ThreadNode key={t.id} item={t} projectId={recordId} projectName={projectName} />
        ))}
      </div>
    )
  }

  /* ── detailZoom — one thread's content, children-only (ZoomTitle shows the title) ── */

  if (vis === 'detailZoom') {
    const id = wf.zoomSection.slice('thread-detail:'.length)
    const t = Array.isArray(threads) ? threads.find(x => x.id === id) : null
    return (
      <div className="wf-list">
        {!threads && <FillerRow text="Loading…" />}
        {Array.isArray(threads) && !t && <FillerRow text="Thread not found." italic />}
        {t && <ThreadNode item={t} projectId={recordId} projectName={projectName} detailZoom />}
      </div>
    )
  }

  /* ── rootZoom ──────────────────────────────────────────────────────────── */

  if (vis === 'rootZoom') {
    // No in-content toolbar: the count shows as ZoomTitle meta and "Add thread" lives in the
    // title ⋯ menu (dispatches open-threads-add, handled below).
    return (
      <div className="wf-list">
        <ThreadListContent
          threads={threads}
          projectId={recordId}
          projectName={projectName}
          vis={vis}
        />

        {showCreate && (
          <CreateThreadModal
            projectId={recordId}
            onClose={() => setShowCreate(false)}
            onCreated={() => {
              setShowCreate(false)
              // threadCache was busted by CreateThreadModal; reload
              hasLoadedRef.current = false
              maybeLoad(recordId)
            }}
          />
        )}
      </div>
    )
  }

  /* ── home / referenceZoom — Reference-child root node ─────────────────── */

  // At project level the root row renders only when Reference is expanded
  // (indented); in Reference zoom it renders plain.
  if (vis === 'home' && wf.collapsed['root:reference'] !== false) return null

  const rootNode = (
      <div
        className={`wf-node wf-node--root wf-node--root-threads${rootCollapsed ? ' wf-node--collapsed' : ''}`}
        data-key="root:threads"
      >
        <div className="wf-row">
          <button
            className="wf-triangle"
            onClick={e => {
              e.stopPropagation()
              const newCollapsed = !rootCollapsed
              wf.collapsed['root:threads'] = newCollapsed
              saveViewState()
              if (!newCollapsed) maybeLoad(recordId) // expanding → ensure threads load
              window.dispatchEvent(new Event('outline-render'))
            }}
          >
            <span className={`wf-tri-icon ${rootCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
          </button>
          <button
            className="wf-bullet wf-bullet--root"
            title="Focus on Threads"
            data-fn="zoomTo"
            data-level="root"
            data-rootname="threads"
          ></button>
          <div className="wf-text-wrap">
            <span className="wf-node-text wf-node-text--root">Threads</span>
            {Array.isArray(threads) && <ThreadCountsMeta threads={threads} />}
          </div>
          <button
            className="wf-menu-btn"
            data-fn="openMenu"
            data-type="root"
            data-key="root:threads"
            data-rootname="threads"
          >&#8943;</button>
        </div>
        {!rootCollapsed && (
          <div className="wf-children">
            <ThreadListContent
              threads={threads}
              projectId={recordId}
              projectName={projectName}
              vis={vis}
            />
          </div>
        )}
      </div>
  )

  return (
    <div className="wf-list">
      {vis === 'referenceZoom' ? rootNode : <div className="wf-ref-child">{rootNode}</div>}

      {showCreate && (
        <CreateThreadModal
          projectId={recordId}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false)
            hasLoadedRef.current = false
            maybeLoad(recordId)
          }}
        />
      )}
    </div>
  )
}
